import { resolverEmpresaProducto } from "./empresaProductoVenta";

// Año ("YYYY") de un periodo "MM-YYYY", o null si no matchea.
const anioPeriodo = (periodo) => {
  const m = (periodo || "").match(/^\d{1,2}-(\d{4})$/);
  return m ? m[1] : null;
};

// Id estable de una unidad reportable a partir de su clave (misma clave que consume
// el motor de reporteFotografico). Venta → "s{sale_id}"; cuota → "c{contrato_id}:{periodo}".
// Acepta tanto una clave { sale_id } | { contrato_id, periodo } como una fila de
// reportes_fotograficos ({ sale_id, contrato_id, periodo }).
export const unidadId = (clave) =>
  clave?.sale_id != null ? `s${clave.sale_id}` : `c${clave.contrato_id}:${clave.periodo}`;

/**
 * Agrupa filas de reportes_fotograficos (con idedificio + created_at) por unidad,
 * vía unidadId. Devuelve Map<unidadId, { ultimaFoto: string|null,
 * edificiosConFoto: Set<number>, total: number }>. Sirve para ordenar la lista de
 * "reportes iniciados" cronológicamente (por ultimaFoto) y para el progreso
 * (edificiosConFoto.size). created_at se compara como string ISO (mismo offset).
 */
export const agruparReportesPorUnidad = (filas) => {
  const mapa = new Map();
  (filas || []).forEach((r) => {
    const uid = unidadId(r);
    if (!mapa.has(uid)) mapa.set(uid, { ultimaFoto: null, edificiosConFoto: new Set(), total: 0 });
    const g = mapa.get(uid);
    g.total += 1;
    if (r.idedificio != null) g.edificiosConFoto.add(r.idedificio);
    if (r.created_at && (!g.ultimaFoto || r.created_at > g.ultimaFoto)) g.ultimaFoto = r.created_at;
  });
  return mapa;
};

/**
 * Lista normalizada de "unidades reportables" — carril único para el embudo y el
 * downstream de Reporte Fotográfico (regla 10.1). Cada unidad lleva su `clave` para
 * el motor por clave de reporteFotografico (getMediaByClave / uploadMediaReporte / etc.).
 *
 * - `ventas`: filas `sales` YA filtradas por sus gates (100%, edificios, país, año,
 *   ejecutivo). Una unidad por fila. Las ventas `eliminada=true` (borradas/convertidas)
 *   se descartan ACÁ, de forma centralizada, para que interna y externa queden
 *   consistentes (simétrico al gate de eliminada que ya aplican los contratos).
 * - `contratos`: filas `sales_contratos` YA filtradas por sus gates de contrato
 *   (100%, eliminada=false, país, ejecutivo). Una unidad por CUOTA elegible dentro de
 *   `meses[]`: la cuota entra solo si su `mes` ("MM-YYYY") es del año `ano` y tiene
 *   `edificios_ids` poblado (no se exige facturación).
 * - `clientesById`: mapa { [clientes.id]: cliente } para resolver empresa/producto por id.
 *
 * Unidad: { id, clave, tipo, empresa, producto, cliente, campana, mes_factura, periodo,
 *   fuenteEdificios, edificiostotales, intermediario, periodopauta, ejecutivo, client_id }.
 * `mes_factura` == `periodo` (para reutilizar consumoLabel/orden del embudo).
 * `fuenteEdificios` es el CSV de ids de edificios de la unidad (venta: edificiosparticipantes;
 * cuota: meses[].edificios_ids).
 */
export const construirUnidadesReportables = (ventas, contratos, clientesById, { ano } = {}) => {
  const unidades = [];

  (ventas || [])
    .filter((v) => v.eliminada !== true)
    .forEach((v) => {
      const { empresa, producto } = resolverEmpresaProducto(v, clientesById);
      const clave = { sale_id: v.id };
      unidades.push({
        id: unidadId(clave),
        clave,
        tipo: "venta",
        empresa,
        producto,
        cliente: v.cliente,
        campana: v.campana || null,
        mes_factura: v.mes_factura || null,
        periodo: v.mes_factura || null,
        fuenteEdificios: v.edificiosparticipantes || "",
        edificiostotales: v.edificiostotales,
        intermediario: v.intermediario || null,
        periodopauta: v.periodopauta || null,
        ejecutivo: v.ejecutivo,
        client_id: v.client_id,
      });
    });

  (contratos || []).forEach((c) => {
    const { empresa, producto } = resolverEmpresaProducto(c, clientesById);
    (c.meses || []).forEach((m) => {
      const periodo = (m?.mes || "").trim();
      const edificios = (m?.edificios_ids || "").trim();
      if (!periodo || !edificios) return; // cuota sin periodo o sin edificios asignados → fuera
      if (ano != null && anioPeriodo(periodo) !== String(ano)) return; // gate de año por periodo
      const clave = { contrato_id: c.id, periodo };
      unidades.push({
        id: unidadId(clave),
        clave,
        tipo: "contrato",
        empresa,
        producto,
        cliente: c.cliente,
        campana: c.campana || null,
        mes_factura: periodo,
        periodo,
        fuenteEdificios: edificios,
        edificiostotales: m?.edificios_totales ?? edificios.split(",").filter(Boolean).length,
        intermediario: null,
        periodopauta: c.periodopauta || null,
        ejecutivo: c.ejecutivo,
        client_id: c.client_id,
      });
    });
  });

  return unidades;
};
